import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import {
  curriculumDetailList,
  projectTypeList,
  projectDetailList,
  projectOutputList,
  benefitDetailList,
  firstCompanyList,
  secondCompanyList,
  benefitSlideList,
  reviewList,
  applySchedule1List,
  applySchedule2List,
  eduScheduleList,
  bootCampFaqList,
  bootCampFaqTabList,
} from '../../../../constants/kdt-backendp-16th'
import Link from 'next/link'
import Image from 'next/image'
import { useMemo, useState } from 'react'
import ReactPlayer from 'react-player'
import { useCopyUrlShare } from 'shared/hooks/useCopyUrlShare'

import CurriculumTab from './CurriculumTab'
import CurriculumAccordion from './CurriculumAccordion'
import ProjectAccordion from './ProjectAccordion'
import BenefitAccordion from './BenefitAccordion'
import { BenefitSliderItem, ProjectOutputSliderItem } from './SliderItem'
import styles from './kdt-python-16th.module.scss'
import { schoolNavList, ScrollNav } from './ScrollNav'
import LazyLoadSection from '@/components/LazyLoadSection'

const Besp16th = () => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [faqTab, setFaqTab] = useState('apply')
  const allFaqs = [
    ...bootCampFaqList.apply,
    ...bootCampFaqList.nebeka,
    ...bootCampFaqList.employment,
    ...bootCampFaqList.edu,
    ...bootCampFaqList.benefit,
    ...bootCampFaqList.etc,
  ]

  const faqList = faqTab === 'all' ? allFaqs : bootCampFaqList[faqTab]
  const onClickFaqTab = (value: string) => {
    setFaqTab(value)
  }
  const earlySessionDate = new Date('2025-04-17 21:00')
  const isAfterEarlySessionDate = useMemo(() => dayjs().isAfter(earlySessionDate), [])
  const { copyUrlShare } = useCopyUrlShare()

  return (
    <div>
      {/* Section1_키비주얼 */}
      <section className="bg-[#1C1B1A]">
        <div className={`${styles.section1_kv} relative h-full w-full`}>
          <img
            className="absolute left-0 top-0 w-full object-cover"
            src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section1_kv_before_${
              isMobile ? 'm' : 'pc'
            }.png`}
            alt="[멋쟁이사자처럼] 백엔드 Python - AI 기반 개발 환경에 최적화된 백엔드 파이썬 개발자 되기"
          />
          <video
            src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section1_kv_video_ver1_${
              isMobile ? 'm' : 'pc'
            }.mp4`}
            muted
            autoPlay
            playsInline
            loop
          ></video>
        </div>
      </section>

      {/* Section2_요약정보 */}
      <LazyLoadSection
        height={943}
        mobileHeight={769}
        className="bg-gray-900 pb-[60px] pt-10 lg:py-[60px]"
      >
        <div className="mx-auto flex w-full max-w-xl flex-col gap-5 px-4 lg:max-w-7xl lg:gap-10 lg:px-6">
          <ul className="flex w-full flex-col">
            <li className="flex w-full flex-row items-start border-b border-label-neutral pb-5 lg:items-center">
              <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 text-base font-semibold text-white lg:w-[calc(33.33%+16px)] lg:gap-4">
                <img
                  className="h-6 w-6 object-contain"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section2_icon1.png"
                  alt="-"
                />
                모집기간
              </h6>
              <p className="text-sm text-white lg:w-2/3 lg:text-base">
                25.03.28 오후 2시 ~ <br className="block lg:hidden" />
                25.04.27 오후 11시
              </p>
            </li>
            <li className="flex w-full flex-row items-center border-b border-label-neutral py-5">
              <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 text-base font-semibold text-white lg:w-[calc(33.33%+16px)] lg:gap-4">
                <img
                  className="h-6 w-6 object-contain"
                  src={
                    'https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section2_icon2.png'
                  }
                  alt="-"
                />
                교육기간
              </h6>
              <p className="text-sm text-white lg:w-2/3 lg:text-base">25.04.30 ~ 25.10.28</p>
            </li>
            <li className="flex w-full flex-row items-start pt-5">
              <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 text-base font-semibold text-white lg:w-[calc(33.33%+16px)] lg:gap-4">
                <img
                  className="h-6 w-6 object-contain"
                  src={
                    'https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section2_icon3.png'
                  }
                  alt="-"
                />
                교육비용
              </h6>
              <p className="text-sm text-white lg:w-2/3 lg:text-base">
                내일배움카드로 교육비 전액 무료 <br className="block lg:hidden" />
                <span className="line-through">16,698,000</span> → 전액 무료
                <br />
                <span className="mt-3 inline-block text-xs leading-normal lg:text-sm">
                  *국민내일배움카드 보유자 한정
                  <br />
                  *매월 최대 81만원의 훈련장려금 신청 가능
                </span>
              </p>
            </li>
          </ul>

          <div className="flex w-full flex-col overflow-hidden rounded-lg border border-yellow-450 bg-[rgba(255,119,16,0.10)] p-3 lg:flex-row lg:px-10 lg:py-8">
            <div className="hidden lg:inline-block lg:lg:w-[calc(33.33%-5px)]">
              <div className="h-[176px] w-[176px]">
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section2_img1_pc.png"
                  layout="fill"
                  objectFit="cover"
                  className="!relative"
                  alt="-"
                />
              </div>
            </div>
            <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-1 lg:gap-5">
              <div className="flex flex-row items-center gap-3 border-b border-[#AB5311] pb-3 lg:border-none lg:pb-0">
                <div className="h-10 w-10 lg:hidden">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section2_img1_m.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="-"
                  />
                </div>
                <h5 className="flex-1 text-base font-semibold text-white lg:text-2xl">지원조건</h5>
              </div>
              <ul className={`${styles.section2_intro_list} flex w-full flex-col lg:gap-2`}>
                <li className="text-sm leading-normal text-white lg:text-base">
                  국민내일배움카드 보유자 혹은 발급 가능하신 분
                </li>
                <li className="text-sm leading-normal text-white lg:text-base">
                  평일 오전 9시부터 오후 6시까지 온라인 전일제 <br className="block lg:hidden" />
                  수업 참여가 가능하신 분
                </li>
                <li className="text-sm leading-normal text-white lg:text-base">
                  Zoom을 통한 온라인 수업에 필요한 웹캠 등의 <br className="block lg:hidden" />
                  장비가 있으신 분
                </li>
                <li className="text-sm leading-normal text-white lg:text-base">
                  직장 혹은 학교 등과 병행하지 않고 수업에만 집중 가능하신 분
                </li>
              </ul>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      <ScrollNav schoolNavList={schoolNavList} />

      {/* Section3_교육 USP */}
      <LazyLoadSection
        height={1199}
        mobileHeight={1336}
        className={`${styles.section3_bg} bg-trueGray-800 pb-[100px] pt-20 lg:pb-[220px] lg:pt-36`}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex w-full flex-col items-center justify-center gap-2 lg:mb-20 lg:items-start lg:justify-start lg:gap-4"
          >
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              백엔드 실무진이 인정한 <br className="block lg:hidden" />
              실무 기반 교육 환경!
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              AI를 활용한 코드 최적화부터 현직 개발자들의 <br className="block lg:hidden" />
              프로젝트 사업성 심사까지 실무 환경을 <br className="hidden lg:block" />
              100% <br className="block lg:hidden" />
              구현한 커리큘럼을 경험해 보세요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="400"
            className="flex flex-col items-stretch justify-center gap-5 lg:flex-row lg:justify-between lg:gap-8"
          >
            <div className="w-full overflow-hidden rounded-lg border border-label-neutral lg:w-auto lg:flex-1">
              <Image
                src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section3_img1_${
                  isMobile ? 'm' : 'pc'
                }.gif`}
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                unoptimized
                alt="-"
              />
            </div>
            <div className="flex w-full flex-col items-center justify-center rounded-lg bg-[#404040] pb-[18px] pt-4 lg:w-auto lg:flex-1 lg:pb-0 lg:pt-0">
              <p className="mb-4 text-xs font-bold text-label-assistive lg:text-xl">
                활용 AI 툴 예시
              </p>
              <div className="mb-2 w-[252px] lg:mb-4 lg:w-[408px]">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section3_img2-1_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="contain"
                  className="!relative w-full"
                  unoptimized
                  alt="-"
                />
              </div>
              <div className="w-[174px] lg:w-[302px]">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section3_img2-2_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="contain"
                  className="!relative w-full"
                  unoptimized
                  alt="-"
                />
              </div>
              <p className="mt-2.5 text-xs text-label-neutral lg:mt-4 lg:text-sm">
                *교육 상황에 따라 활용하는 AI 툴은 변경될 수 있습니다.
              </p>
            </div>
          </div>

          <ul className="mt-10 flex flex-col gap-3 lg:flex-row lg:gap-[25px]">
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex w-full flex-col gap-3 rounded-lg bg-white px-4 py-6 lg:w-auto lg:flex-1 lg:gap-5 lg:px-5 lg:py-6"
            >
              <p className="flex max-w-fit items-center justify-center rounded bg-[rgba(255,119,16,0.15)] px-2 py-0.5 text-xs font-bold text-yellow-450 lg:px-3 lg:py-1 lg:text-sm">
                포인트 1
              </p>
              <p className="text-sm font-medium leading-normal text-label-normal lg:text-base">
                ChatGPT부터 Cursor까지 다양한 AI IDE 활용법을
                <br />
                배우고 AI에 의존하여 코드를 생성하는 것이 아닌
                <br />
                코드 리뷰 및 오류 해결 등의 생산성 향상 목적의 <br className="block lg:hidden" />
                활용 <br className="hidden lg:block" />
                방법을 배워요
              </p>
            </li>
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="800"
              className="flex w-full flex-col gap-3 rounded-lg bg-white px-4 py-6 lg:w-auto lg:flex-1 lg:gap-5 lg:px-5 lg:py-6"
            >
              <p className="flex max-w-fit items-center justify-center rounded bg-[rgba(255,119,16,0.15)] px-2 py-0.5 text-xs font-bold text-yellow-450 lg:px-3 lg:py-1 lg:text-sm">
                포인트 2
              </p>
              <p className="text-sm font-medium leading-normal text-label-normal lg:text-base">
                실제 기업이 수행한 프로젝트를 동일하게 경험하며
                <br />
                이론으로만 기술 스택을 배우는 것이 아니라 실무에서
                <br />
                활용하는 방식까지 전문적으로 배워요
              </p>
            </li>
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="1000"
              className="flex w-full flex-col gap-3 rounded-lg bg-white px-4 py-6 lg:w-auto lg:flex-1 lg:gap-5 lg:px-5 lg:py-6"
            >
              <p className="flex max-w-fit items-center justify-center rounded bg-[rgba(255,119,16,0.15)] px-2 py-0.5 text-xs font-bold text-yellow-450 lg:px-3 lg:py-1 lg:text-sm">
                포인트 3
              </p>
              <p className="text-sm font-medium leading-normal text-label-normal lg:text-base">
                취업 특강부터 프로젝트 사업성 심사까지 현직
                <br />
                개발자들이 직접 진행하여 사소한 부분까지 <br className="block lg:hidden" />
                놓치지않고 밀착 케어해요
              </p>
            </li>
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section4_커리큘럼상세 */}
      <LazyLoadSection
        height={1747}
        mobileHeight={2029}
        id="curriculum"
        className={`${styles.section4_bg} bg-gray-900 pb-[200px] pt-20 lg:pb-[296px] lg:pt-36`}
      >
        <div className="mx-auto w-full max-w-xl lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16 lg:items-start lg:justify-start lg:gap-4"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">커리큘럼</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              현직 백엔드 개발자들의
              <br />
              업무 방식까지 담은 실무 <br className="block lg:hidden" />
              최적화 커리큘럼
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              실습을 통한 기본기 학습은 물론, 현직 백엔드 개발자들의
              <br />
              ChatGPT, Copilot, Cursor 등의 <br className="block lg:hidden" />
              AI 툴 기반 실무 활용법까지 배워보세요!
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.curriculum_accordion_wrap} hidden flex-col items-start overflow-hidden lg:flex`}
          >
            <CurriculumTab contents={curriculumDetailList} />
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.curriculum_accordion_wrap} flex flex-col overflow-hidden lg:hidden`}
          >
            {curriculumDetailList.map((curriculum, idx) => (
              <CurriculumAccordion contents={curriculum} key={curriculum.title} idx={idx} />
            ))}
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section5_프로젝트 */}
      <LazyLoadSection
        height={2165}
        mobileHeight={2538}
        id="project"
        className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-[60px] flex flex-col items-center justify-center gap-2 lg:mb-20 lg:items-start lg:justify-start lg:gap-4"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">프로젝트</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              실제 기업에서 진행한 <br className="block lg:hidden" />
              프로젝트를 <br className="hidden lg:block" />내 포트폴리오에{' '}
              <br className="block lg:hidden" />
              담을 수 있는 유일한 기회!
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              실제 기업 프로젝트와 동일하게 구현된 <br className="block lg:hidden" />
              프로젝트 구성을 통해 <br className="hidden lg:block" />
              실무 경험 그 이상의 <br className="block lg:hidden" />
              가치를 경험해 보세요!
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-8 lg:mb-10"
          >
            <h6 className="text-center text-lg font-bold text-label-assistive lg:text-left lg:text-2xl">
              프로젝트 구성
            </h6>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col gap-6 lg:flex-row lg:items-stretch lg:justify-center lg:gap-8"
          >
            {projectTypeList.map(projectType => (
              <li
                className="flex w-full flex-col justify-between overflow-hidden rounded-lg bg-[#232323] lg:w-full lg:flex-1"
                key={projectType.title}
              >
                <div>
                  <div className="w-full">
                    <Image
                      src={isMobile ? projectType.imgUrlM : projectType.imgUrlPc}
                      layout="fill"
                      objectFit="cover"
                      className="!relative w-full"
                      unoptimized
                      alt="-"
                    />
                  </div>
                  <div className="flex flex-col gap-4 px-4 py-6">
                    <p className="max-w-fit rounded bg-[rgba(255,119,16,0.15)] px-3 py-1 text-sm font-bold text-yellow-450 lg:py-[4.5px]">
                      {projectType.type}
                    </p>
                    <div className="flex flex-col gap-2">
                      <h6 className="text-lg font-bold leading-normal text-white lg:text-xl">
                        {projectType.title}
                      </h6>
                      <ul className="flex flex-col gap-0.5">
                        {projectType.descList.map((desc1: string) => (
                          <li
                            key={desc1}
                            className={`${styles.text_dot} ${styles.text_dot_section5} text-sm leading-normal text-label-assistive lg:text-base`}
                          >
                            {desc1}
                          </li>
                        ))}
                      </ul>
                      {projectType.noti && (
                        <p className="mt-1 text-xs text-label-neutral lg:text-sm">
                          {projectType.noti}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <dl className="mx-4 mb-6 flex flex-col gap-3 rounded bg-[#333] px-4 py-3 lg:min-h-[256px] lg:gap-2">
                  <dt className="text-sm font-semibold text-white lg:text-base">
                    현직 백엔드 개발자의 Tip!
                  </dt>
                  {projectType.descList2.map((desc2: string) => (
                    <dd
                      key={desc2}
                      className={`${styles.text_dot} ${styles.text_dot_section5_2} text-sm leading-normal text-label-alternative lg:text-base`}
                    >
                      {desc2}
                    </dd>
                  ))}
                </dl>
              </li>
            ))}
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-8 mt-[60px] lg:mb-10 lg:mt-20"
          >
            <h6 className="text-center text-lg font-bold text-label-assistive lg:text-left lg:text-2xl">
              기능 구현 예시
            </h6>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex flex-col overflow-hidden"
          >
            {projectDetailList.map((project, idx) => (
              <ProjectAccordion contents={project} key={project.title} idx={idx} />
            ))}
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section6_수료생 프로젝트 결과물 */}
      <LazyLoadSection
        height={1039}
        mobileHeight={623}
        className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mx-auto mb-[60px] flex w-full max-w-xl flex-col items-center justify-center gap-2 px-4 lg:mb-20 lg:max-w-7xl lg:items-start lg:justify-start lg:gap-4 lg:px-6"
          >
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              수료생 프로젝트 결과물
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              지난 수료생들의 프로젝트 결과물처럼 누구나 기초부터 <br className="block lg:hidden" />
              차근차근 배워 <br className="hidden lg:block" />
              나만의 포트폴리오를 만들어 보세요!
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.section6_slider}`}
          >
            <ProjectOutputSliderItem contents={projectOutputList} />
          </div>
        </div>
      </LazyLoadSection>

      {/* Section7_사전설명회 */}
      {isAfterEarlySessionDate ? (
        <LazyLoadSection
          className="bg-[#FFF5EF] pb-24 pt-20 lg:pb-40 lg:pt-36"
          height={1145}
          mobileHeight={469}
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16 lg:gap-4"
            >
              <h4 className="text-center text-2xl font-bold text-label-normal lg:text-4xl">
                사전설명회 다시보기
              </h4>
              <p className="text-center text-sm leading-normal text-label-normal lg:block lg:text-lg">
                강사님과 실시간 라이브로 진행된 사전설명회 다시보기
              </p>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex flex-col"
            >
              <div
                className="h-auto overflow-hidden"
                style={{
                  position: 'relative',
                  paddingTop: '56.25%',
                }}
              >
                <ReactPlayer
                  alt="멋쟁이사자처럼 사전설명회 LIVE"
                  url="https://www.youtube.com/live/mC4WgcYti4s"
                  controls={true}
                  width="100%"
                  height="100%"
                  muted
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </div>
            </div>
          </div>
        </LazyLoadSection>
      ) : (
        <LazyLoadSection
          className="bg-[#FFF5EF] pb-24 pt-20 lg:py-40"
          height={628}
          mobileHeight={624}
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:flex lg:max-w-7xl lg:flex-row-reverse lg:items-center lg:justify-between lg:gap-[128px] lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="w-full lg:w-1/2 lg:min-w-[400px] lg:max-w-[459px]"
            >
              <div className="flex w-full flex-col items-center justify-center lg:items-start">
                <h4 className="mb-3 text-center text-2xl font-bold text-label-normal lg:mb-8 lg:text-left lg:text-4xl">
                  사전설명회 안내
                </h4>
                <p className="mb-10 text-center text-sm leading-normal text-label-normal lg:mb-4 lg:text-left lg:text-lg">
                  백엔드 Python 16기의 자세한 수업 방식부터 <br className="block lg:hidden" />
                  단독 혜택까지 <br className="hidden lg:block" />단 한 번만 진행되는{' '}
                  <br className="block lg:hidden" />
                  실시간 사전설명회에서 모두 확인해 보세요!
                </p>
                <div className="mb-5 w-full overflow-hidden rounded lg:hidden">
                  <img
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section7_img_m.gif"
                    className="h-full w-full object-contain"
                    alt="*참고용 사전설명회 진행 모습"
                  />
                </div>
                <p className="text-center text-sm font-bold leading-normal text-yellow-450 lg:text-left lg:text-lg">
                  일정 : 04월 17일(목) 오후 7시
                  <br />
                  장소 : Youtube 라이브 스트리밍
                </p>
              </div>
              <div className="mt-5 lg:mt-10">
                <Link
                  href="https://likelion2.typeform.com/to/fdYIStZ0"
                  target="_blank"
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-450 py-3.5 text-sm font-bold text-white lg:max-w-[395px] lg:gap-2.5 lg:py-5 lg:text-lg"
                >
                  사전설명회 신청하기
                  <img
                    className="h-4 w-4 lg:h-[22px] lg:w-[22px]"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-design-5th/pre-orientation_icon.png"
                    alt="사전설명회"
                  />
                </Link>
              </div>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="hidden h-auto overflow-hidden rounded-lg lg:inline-block"
            >
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section7_img_pc.gif"
                className="h-full w-full object-contain"
                alt="*참고용 사전설명회 진행 모습"
              />
            </div>
          </div>
        </LazyLoadSection>
      )}

      {/* Section8_강사진 */}
      <LazyLoadSection
        height={1747}
        mobileHeight={2029}
        id="teacher"
        className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:flex lg:max-w-7xl lg:flex-row lg:gap-4 lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-0 lg:min-w-[500px] lg:items-start lg:justify-center lg:gap-4"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">강사소개</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              여러분의 성장을 함께할
              <br />
              최고의 강사님
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              Python 기본기부터 실무 기반 포트폴리오 제작까지 <br className="block lg:hidden" />
              모든 과정을 <br className="hidden lg:block" />약 20년 경력의 강사님과 함께하니{' '}
              <br className="block lg:hidden" />
              걱정 말고 지원해 보세요!
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col overflow-hidden rounded-lg bg-trueGray-800 p-4 lg:w-auto lg:min-w-[717px] lg:flex-1 lg:flex-row lg:gap-6 lg:p-10"
          >
            <div className="hidden h-[180px] border-r border-[#525252] pr-6 lg:inline-block">
              <div className="h-[180px] w-[180px]">
                <Image
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section8_img1_pc.png"
                  layout="fill"
                  objectFit="cover"
                  className="!relative w-full"
                  alt="-"
                />
              </div>
            </div>
            <div className="flex w-full flex-col gap-4 lg:w-auto lg:flex-1 lg:gap-6">
              <div className="flex flex-row items-center gap-4 border-b border-[#404040] pb-4 lg:border-0 lg:pb-0">
                <div className="h-[70px] w-[70px] lg:hidden">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/section8_img1_m.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="-"
                  />
                </div>
                <div className="flex flex-col lg:flex-row-reverse lg:items-center lg:gap-3">
                  <p className="text-xs font-bold text-yellow-450 lg:text-sm">주강사</p>
                  <p className="text-base font-bold text-white lg:text-2xl">이유정 강사님</p>
                </div>
              </div>
              <ul className="flex flex-col gap-2 lg:gap-1">
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-assistive`}
                >
                  NIA 한국정보진흥원 주관 로봇 명령어 데이터셋 구축 및 시뮬레이션 개발
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-assistive`}
                >
                  한국관광공사 주관 안목해변 동선 분석 데이터 개발
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-assistive`}
                >
                  국토교통부 주관 제물포 VR/AR 플랫폼 개발
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-assistive`}
                >
                  공항 이용자 대상의 지오펜싱 기반 앱 개발
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section8} text-sm leading-normal text-label-assistive`}
                >
                  그 외 각종 이커머스 홈페이지 구축 및 유지보수 진행
                </li>
              </ul>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section9_수료후기 */}
      <LazyLoadSection
        height={1346}
        mobileHeight={1458}
        id="review"
        className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-[60px] flex flex-col items-center justify-center gap-2 lg:mb-16 lg:items-start lg:justify-start lg:gap-4"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">수료생 후기</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              수료생 후기 모음
            </h4>
          </div>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex w-full flex-col gap-4 lg:gap-6"
          >
            {reviewList.map(reviews => (
              <li className="flex w-full flex-col items-center gap-4 rounded-lg bg-[#303030] p-4 lg:flex-row lg:gap-6 lg:px-6 lg:py-5">
                <div className="flex w-full flex-row items-center gap-4 border-b border-[#525252] pb-4 pr-6 lg:w-auto lg:max-w-fit lg:border-b-0 lg:border-r lg:pb-0 lg:pr-6">
                  <img
                    className="h-12 w-12 shrink-0 overflow-hidden lg:h-[84px] lg:w-[84px]"
                    src={reviews.imgUrl}
                    alt="-"
                  />
                  <h6 className="text-lg font-bold leading-normal text-white lg:hidden lg:text-xl">
                    {reviews.title}
                  </h6>
                </div>
                <div className="flex w-full flex-col items-start justify-start gap-1 lg:w-auto lg:flex-1 lg:gap-3">
                  <h6 className="hidden text-lg font-bold text-white lg:block lg:text-xl">
                    {reviews.title}
                  </h6>
                  <p className="whitespace-pre-line text-left text-base leading-normal text-white">
                    {reviews.desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section10_취업혜택 */}
      <LazyLoadSection
        height={1632}
        mobileHeight={859}
        className={`${styles.section10_bg} bg-gray-900 pb-[140px] pt-20 lg:pb-[189px] lg:pt-36`}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-11 flex flex-col items-center justify-center gap-2 lg:mb-20 lg:items-start lg:justify-start lg:gap-4"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">취업 혜택</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              오직 여러분의 취업을 위해 <br className="block lg:hidden" />
              준비한 혜택 모음
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              멋쟁이사자처럼만의 채용 연계 혜택은 물론 <br className="block lg:hidden" />
              인사팀장 출신 커리어 코치가 <br className="hidden lg:block" />
              교육 시작부터 <br className="block lg:hidden" />
              수료 후까지 여러분의 취업을 위해 함께 하니 걱정 마세요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-8 lg:mb-10"
          >
            <h6 className="text-center text-lg font-bold text-label-assistive lg:text-left lg:text-2xl">
              취업 지원 프로그램 구성
            </h6>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className="flex flex-col overflow-hidden"
          >
            {benefitDetailList.map((benefit, idx) => (
              <BenefitAccordion contents={benefit} key={benefit.title} idx={idx} />
            ))}

            <p className="mt-10 text-center text-xs leading-normal text-label-assistive lg:mt-12 lg:text-left lg:text-base lg:text-label-alternative">
              * 교육생 훈련 상황 및 회사 사정에 따라 지원 내용이 <br className="block lg:hidden" />
              변동 될 수 있습니다.
            </p>
          </ul>
        </div>
      </LazyLoadSection>

      <LazyLoadSection
        height={433}
        mobileHeight={255}
        className="bg-white py-9 lg:pb-[106px] lg:pt-16"
      >
        <div className="mx-auto w-full">
          <h6
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mb-8 text-center text-base font-semibold text-label-normal lg:mb-16 lg:text-2xl lg:font-bold"
          >
            수료생 합격 기업 모음
          </h6>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.company_logo_flow_slider_wrap} flex flex-col overflow-hidden`}
          >
            <div className={`${styles.company_logo_flow_slider}`}>
              <div className={`${styles.company_logo_flow_slider_track}`}>
                {[...firstCompanyList, ...firstCompanyList, ...firstCompanyList].map(
                  (item, idx) => (
                    <div key={`${item}_${idx}`} className={styles.slider_item}>
                      <img className="h-full w-full object-contain" src={item} alt="company_logo" />
                    </div>
                  ),
                )}
              </div>
            </div>

            <div
              className={`${styles.company_logo_flow_slider} ${styles.company_logo_flow_slider_second}`}
            >
              <div className={`${styles.company_logo_flow_slider_track}`}>
                {[...secondCompanyList, ...secondCompanyList, ...secondCompanyList].map(
                  (item, idx) => (
                    <div key={`${item}_${idx}`} className={styles.slider_item}>
                      <img className="h-full w-full object-contain" src={item} alt="company_logo" />
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section11_교육혜택 */}
      <LazyLoadSection
        height={1051}
        mobileHeight={723}
        className="bg-[#333333] pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="mx-auto mb-[60px] flex w-full max-w-xl flex-col items-center justify-center gap-2 px-4 lg:mb-16 lg:max-w-7xl lg:items-start lg:justify-start lg:gap-4 lg:px-6"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">교육 혜택</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              비용 걱정 NO! 장비 걱정 NO!
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              멋쟁이사자처럼이 준비한 커리큘럼에 다양한 교육 혜택을{' '}
              <br className="block lg:hidden" />
              더해 <br className="hidden lg:block" />
              여러분은 오직 교육에만 집중할 수 있도록 도와드릴게요
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="600"
            className={`${styles.benefit_slider}`}
          >
            <BenefitSliderItem contents={benefitSlideList} />
          </div>
        </div>
      </LazyLoadSection>

      {/* section12_일정안내 */}
      <LazyLoadSection
        height={543}
        mobileHeight={673}
        id="condition"
        className="bg-[#FFF2E9] py-20 lg:pb-36 lg:pt-[120px]"
      >
        <div className="mx-auto flex w-full max-w-lg flex-col gap-10 px-4 lg:mx-auto lg:max-w-7xl lg:flex-row lg:gap-6 lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="w-full lg:w-auto lg:flex-1"
          >
            <div className="mb-6 lg:mb-8">
              <h4 className="text-center text-2xl font-bold text-label-normal lg:text-3xl">
                지원 및 선발 안내
              </h4>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="flex flex-col"
            >
              <div className="relative flex w-full flex-col lg:hidden">
                {applySchedule1List.map(edu => (
                  <dl className="flex flex-col gap-2 rounded-lg bg-white px-4 py-3" key={edu.title}>
                    <dt className="w-full text-sm font-semibold leading-normal text-yellow-450">
                      {edu.title}
                    </dt>
                    <dd className="whitespace-pre-line text-sm leading-normal text-label-normal">
                      {edu.content}
                    </dd>
                  </dl>
                ))}
              </div>

              <div className="relative mt-3 flex w-full flex-col lg:hidden">
                <div className="flex flex-col gap-2 rounded-lg bg-white px-4 py-3">
                  {applySchedule2List.map(edu => (
                    <dl
                      key={edu.title}
                      className="flex flex-col gap-2 border-b border-[#E0DFDE] pb-2 last-of-type:border-0 last-of-type:pb-0"
                    >
                      <dt className="w-full text-sm font-semibold leading-normal text-yellow-450">
                        {edu.title}
                      </dt>
                      <dd className="whitespace-pre-line text-sm leading-normal text-label-normal">
                        <div className="flex flex-row items-center gap-4">{edu.content}</div>
                      </dd>
                    </dl>
                  ))}
                </div>
              </div>

              <div className="relative hidden w-full flex-col lg:flex">
                <div className="rounded-lg bg-white px-6 py-5 lg:min-h-[211px]">
                  {applySchedule1List.map(edu => (
                    <dl
                      className="flex flex-row items-center gap-5 border-b border-[#E0DFDE] py-3 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0"
                      key={edu.title}
                    >
                      <dt className="w-[95px] whitespace-pre-line text-base font-bold leading-normal text-yellow-450">
                        {edu.title}
                      </dt>
                      <dd className="flex-1 whitespace-pre-line text-left text-base leading-normal text-label-normal">
                        {edu.content}
                      </dd>
                    </dl>
                  ))}
                  {applySchedule2List.map(edu => (
                    <dl
                      className="flex flex-row items-start gap-5 border-b border-[#E0DFDE] py-3 first-of-type:items-center first-of-type:pt-0 last-of-type:items-center last-of-type:border-0 last-of-type:pb-0"
                      key={edu.title}
                    >
                      <dt className="w-[95px] whitespace-pre-line text-base font-bold leading-normal text-yellow-450">
                        {edu.title}
                      </dt>
                      <dd className="flex-1 whitespace-pre-line text-left text-base leading-normal text-label-normal">
                        <div className="flex flex-row items-center gap-4">{edu.content}</div>
                      </dd>
                    </dl>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="800"
            data-aos-delay="300"
            className="w-full lg:w-auto lg:flex-1"
          >
            <div className="mb-6 lg:mb-8">
              <h4 className="text-center text-2xl font-bold text-label-normal lg:text-3xl">
                교육 일정 안내
              </h4>
            </div>

            <ul
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className="rounded-lg bg-white px-5 py-4 lg:px-6 lg:py-5"
            >
              {eduScheduleList.map(edu => (
                <li
                  className="flex flex-row items-start justify-center gap-5 border-b border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-4 lg:py-3"
                  key={edu.title}
                >
                  <b className="w-[53px] shrink-0 whitespace-pre-line text-sm font-semibold leading-normal text-yellow-450 lg:w-[76px] lg:text-base lg:font-bold">
                    {edu.title}
                  </b>
                  <p className="flex-1 whitespace-pre-line text-sm leading-normal text-label-normal lg:whitespace-normal lg:text-base">
                    {edu.content}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </LazyLoadSection>

      {/* section13_자주묻는질문 */}
      <LazyLoadSection className="bg-white pb-24 pt-20 lg:pb-40 lg:pt-36">
        <div className="mx-auto w-full max-w-7xl px-4 lg:px-6">
          <h4 className="mb-6 text-center text-2xl font-bold text-label-normal lg:text-4xl">
            자주 묻는 질문
          </h4>
          <ul className="mx-auto mb-10 flex max-w-[324px] flex-wrap items-center justify-center gap-1.5 lg:mb-16 lg:max-w-none lg:flex-nowrap lg:gap-2">
            {bootCampFaqTabList.map(tabItem => {
              const isActive = tabItem.value === faqTab
              return (
                <li
                  className={`${
                    isActive
                      ? 'bg-yellow-450 text-white'
                      : 'border border-normal bg-white text-gray-400'
                  } cursor-pointer rounded-full px-5 py-1.5 text-xs font-bold lg:px-6 lg:py-2 lg:text-sm`}
                  onClick={() => onClickFaqTab(tabItem.value)}
                  key={tabItem.value}
                >
                  {tabItem.label}
                </li>
              )
            })}
          </ul>
          <ul className="mx-auto flex flex-col">
            {faqList.map(faq => (
              <li
                className={`${styles.faqItem} border-b border-[#E5E5E5] py-4 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-6`}
                key={faq.question}
              >
                <details className="group overflow-hidden">
                  <summary className="cursor-pointer list-none group-open:pb-1 lg:group-open:pb-2">
                    <div className="flex w-full items-center justify-between gap-4">
                      <div>
                        <p
                          className={`${styles.faq_item_title} text-sm font-semibold text-label-normal lg:text-lg`}
                        >
                          {faq.question}
                        </p>
                      </div>
                      <ChevronDownIcon className="w-4 shrink-0 text-gray-500 group-open:rotate-180 lg:w-6" />
                    </div>
                  </summary>
                  <div className="pl-5 lg:pl-[22px]">
                    <div className="whitespace-pre-wrap text-sm leading-normal text-gray-500 lg:text-base">
                      {faq.answer && (
                        <span className="max-w-full">
                          {faq.answer}
                          {faq.externalUrl1 && (
                            <>
                              {' '}
                              자세한 발급 과정은 아래 링크를 통해 확인 가능합니다.
                              <br />-{' '}
                              <Link href={faq.externalUrl1} target="_blank" className="underline">
                                {' '}
                                {faq.urlName1}
                              </Link>
                              {faq.secondAnswer}
                            </>
                          )}
                          {faq.externalUrl2 && (
                            <>
                              <br />-{' '}
                              <Link href={faq.externalUrl2} target="_blank" className="underline">
                                {' '}
                                {faq.urlName2}
                              </Link>
                            </>
                          )}
                        </span>
                      )}
                      {faq.answerList &&
                        faq.answerList.map(el => (
                          <p key={el} className="text-sm text-gray-500 lg:text-base">
                            {el}
                          </p>
                        ))}
                    </div>
                  </div>
                </details>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 rounded-md bg-[#FFF3EB] p-4 lg:mt-16 lg:flex-row lg:items-center lg:rounded-lg lg:p-6">
            <p className="w-full text-left text-base font-semibold text-label-normal lg:w-auto lg:flex-1 lg:text-lg lg:font-bold">
              더 궁금한 점이 있다면 <br className="block lg:hidden" />
              언제든지 교육 매니저에게 물어보세요!
            </p>

            <Link
              href="https://likelion.channel.io/workflows/742687"
              target="_blank"
              className="flex h-[36px] w-full shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded bg-white lg:h-[64px] lg:w-[369px] lg:rounded-lg"
            >
              <span className="text-base font-bold text-yellow-450 lg:text-lg">1:1 문의하기</span>
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/icon_go_link.png"
                className="h-[18px] w-[18px] shrink-0 object-contain lg:h-4 lg:w-4"
                alt="1:1 문의하기"
              />
            </Link>
          </div>
          <div className="mt-4 flex flex-col items-center justify-between gap-3 rounded-md bg-strong p-4 lg:mt-5 lg:flex-row lg:items-center lg:rounded-lg lg:p-6">
            <p className="w-full text-left text-base font-semibold text-label-normal lg:w-auto lg:flex-1 lg:text-lg lg:font-bold">
              백엔드 개발자로 함께 성장하고 싶은 친구에게 <br className="block lg:hidden" />
              공유해 보세요!
            </p>
            <div
              className="flex h-[36px] w-full shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded bg-white lg:h-[64px] lg:w-[369px] lg:rounded-lg"
              onClick={() => copyUrlShare('https://bootcamp.likelion.net/school/kdt-backendp-16th')}
            >
              <span className="text-base font-bold text-yellow-450 lg:text-lg">링크 복사하기</span>
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/icon_copy_share.png"
                className="h-[18px] w-[18px] shrink-0 object-contain lg:h-4 lg:w-4"
              />
            </div>
          </div>
        </div>
      </LazyLoadSection>
    </div>
  )
}

export default Besp16th
